import React, { useEffect, useRef, useState } from 'react';
import { CandlestickSeries, createChart, LineSeries } from 'lightweight-charts';
import { CandlestickChart, LineChart, Loader2 } from 'lucide-react';
import { assetsService } from '../../services/assets.service';
import { useLivePrice } from '../../lib/price-feed/PriceFeedProvider';
import { CandleAggregator } from '../../lib/trading-chart/core/candle-aggregator';

const RANGES = [
  { value: '1d', label: '1D', resolution: '5', countBack: 120 },
  { value: '1w', label: '1W', resolution: '30', countBack: 160 },
  { value: '1m', label: '1M', resolution: '240', countBack: 180 },
  { value: '1y', label: '1Y', resolution: '1D', countBack: 260 },
];

const toSeconds = (value) => {
  if (typeof value === 'number') return Math.floor(value > 1e11 ? value / 1000 : value);
  return Math.floor(new Date(value).getTime() / 1000);
};

const normalizeBar = (bar) => ({
  time: toSeconds(bar.time ?? bar.timestamp),
  open: Number(bar.open ?? bar.price),
  high: Number(bar.high ?? bar.price),
  low: Number(bar.low ?? bar.price),
  close: Number(bar.close ?? bar.price),
});

/**
 * Turn GET /v1/assets/:id/timeseries points ({ timestamp, price }) into chart
 * bars. Points carry one price each, so each bar opens at the previous close
 * (keeps the candle view readable instead of flat dashes). Bars that already
 * carry OHLC keep it. Sorted ascending, one bar per second (charts require it).
 */
export const pointsToBars = (points) => {
  const bars = (Array.isArray(points) ? points : [])
    .map(normalizeBar)
    .filter((bar) => Number.isFinite(bar.time) && Number.isFinite(bar.close))
    .sort((a, b) => a.time - b.time);
  const out = [];
  bars.forEach((bar) => {
    const prev = out[out.length - 1];
    if (prev && prev.time === bar.time) {
      out[out.length - 1] = { ...prev, high: Math.max(prev.high, bar.high), low: Math.min(prev.low, bar.low), close: bar.close };
      return;
    }
    const hasOhlc = bar.open !== bar.close || bar.high !== bar.close || bar.low !== bar.close;
    if (hasOhlc || !prev) {
      out.push(bar);
      return;
    }
    const open = prev.close;
    out.push({ ...bar, open, high: Math.max(open, bar.close), low: Math.min(open, bar.close) });
  });
  return out;
};

const AssetMiniChart = ({ assetId, symbol, market, chartHeightClass = 'h-24' }) => {
  const [range, setRange] = useState('1m');
  const [style, setStyle] = useState('line');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const containerRef = useRef(null);
  const seriesRef = useRef(null);
  const aggregatorRef = useRef(null);
  const live = useLivePrice(symbol, market);
  const liveRef = useRef(live);
  liveRef.current = live;
  const selectedRange = RANGES.find((item) => item.value === range);

  useEffect(() => {
    if (!assetId) {
      setData([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    // The partner gateway serves price history at /timeseries (there is no /bars route).
    assetsService
      .getTimeseries(assetId, selectedRange.value)
      .then((result) => {
        if (cancelled) return;
        setData(pointsToBars(result));
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [assetId, selectedRange]);

  useEffect(() => {
    if (!containerRef.current || data.length < 2) return undefined;
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: { background: { color: '#ffffff' }, textColor: '#64748b', attributionLogo: false },
      grid: { vertLines: { color: '#f1f5f9' }, horzLines: { color: '#f1f5f9' } },
      rightPriceScale: { borderColor: '#e2e8f0' },
      timeScale: { borderColor: '#e2e8f0', timeVisible: range === '1d' || range === '1w' },
    });
    const series =
      style === 'line'
        ? chart.addSeries(LineSeries, { color: '#4f46e5', lineWidth: 2 })
        : chart.addSeries(CandlestickSeries, {
            upColor: '#16a34a',
            downColor: '#dc2626',
            borderVisible: false,
            wickUpColor: '#16a34a',
            wickDownColor: '#dc2626',
          });
    series.setData(style === 'line' ? data.map(({ time, close }) => ({ time, value: close })) : data);
    chart.timeScale().fitContent();
    seriesRef.current = series;
    aggregatorRef.current = new CandleAggregator(selectedRange.resolution, data[data.length - 1], 4);
    const initialLive = liveRef.current;
    if (initialLive) {
      const result = aggregatorRef.current.apply({
        timeMs: initialLive.receivedAt ?? initialLive.timestamp ?? Date.now(),
        price: initialLive.price,
        volume: initialLive.volume,
      });
      if (result) {
        series.update(
          style === 'line' ? { time: result.bar.time, value: result.bar.close } : result.bar,
        );
      }
    }
    return () => {
      seriesRef.current = null;
      aggregatorRef.current = null;
      chart.remove();
    };
  }, [data, range, selectedRange, style]);

  useEffect(() => {
    if (!live || !seriesRef.current || !aggregatorRef.current) return;
    const result = aggregatorRef.current.apply({
      timeMs: live.receivedAt ?? live.timestamp ?? Date.now(),
      price: live.price,
      volume: live.volume,
    });
    if (!result) return;
    seriesRef.current.update(
      style === 'line' ? { time: result.bar.time, value: result.bar.close } : result.bar,
    );
  }, [live, style]);

  if (!assetId) return null;

  return (
    <div className="rounded-lg border border-ink-200 bg-white p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">Price history</p>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Line chart" onClick={() => setStyle('line')} className={`rounded p-1 ${style === 'line' ? 'bg-accent-50 text-accent-700' : 'text-ink-400'}`}>
            <LineChart className="h-3.5 w-3.5" />
          </button>
          <button type="button" aria-label="Candlestick chart" onClick={() => setStyle('candles')} className={`rounded p-1 ${style === 'candles' ? 'bg-accent-50 text-accent-700' : 'text-ink-400'}`}>
            <CandlestickChart className="h-3.5 w-3.5" />
          </button>
          <div className="inline-flex rounded-md border border-ink-200 bg-ink-50 p-0.5">
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRange(r.value)}
              className={`px-2 py-0.5 rounded text-2xs font-medium transition-colors ${
                range === r.value ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-700'
              }`}
            >
              {r.label}
            </button>
          ))}
          </div>
        </div>
      </div>
      <div className={chartHeightClass}>
        {loading ? (
          <div className="h-full flex items-center justify-center text-ink-400">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : error ? (
          <div className="h-full flex items-center justify-center text-xs text-ink-400">Chart unavailable</div>
        ) : data.length < 2 ? (
          <div className="h-full flex items-center justify-center text-xs text-ink-400">No price history yet</div>
        ) : (
          <div ref={containerRef} className="h-full w-full" data-testid="lightweight-chart" />
        )}
      </div>
    </div>
  );
};

export default AssetMiniChart;
